/**
 * @file LossHepatitisImmunityListView.jsx
 * @author Daniel Aguirre
 * @description Loss Hepatitis Immunity ListView Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function LossHepatitisImmunityListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = React.useState(totalItemsParams);
  const [dataFilter, setDataFilter] = React.useState(null);
  const [columns, setColumns] = React.useState([]);
  const [filter, setFilter] = React.useState(null);
  const [sorter, setSorter] = React.useState(null);

  const { getLossHepatitisImmunityListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.serostateClasificationHB'),
      dataIndex: 'clasificacion_seroestado_hepatitisb',
      filter: {
        listName: 'listaClasificacionSeroestadoHepatitisB',
        searchName: 'filtroClasificacionSeroestadoHepatitisB',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('clasificacion_seroestado_hepatitisb'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaClasificacionSeroestadoHepatitisB',
            searchName: 'filtroClasificacionSeroestadoHepatitisB'
          },
          dataIndex: 'clasificacion_seroestado_hepatitisb'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.vaccinationSchedulingHB'),
      dataIndex: 'esquema_hepatitisb',
      filter: {
        listName: 'listaEsquemaVacunacionHepatitisB',
        searchName: 'filtroEsquemaVacunacionHepatitisB',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('esquema_hepatitisb'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaEsquemaVacunacionHepatitisB',
            searchName: 'filtroEsquemaVacunacionHepatitisB'
          },
          dataIndex: 'esquema_hepatitisb'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.lastHBSUp10'),
      dataIndex: 'antihbs_mayor10',
      filter: {
        listName: 'listaUltimoHbsMayor10',
        searchName: 'filtroUltimoHbsMayor10',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultima_dosis3'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaUltimoHbsMayor10',
            searchName: 'filtroUltimoHbsMayor10'
          },
          dataIndex: 'antihbs_mayor10'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.dateLastHBSUp10'),
      dataIndex: 'fecha_antihbs_mayor10',
      filter: {
        listName: 'listaFechaUltimoHbsMayor10',
        searchName: 'filtroFechaUltimoHbsMayor10',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_antihbs_mayor10'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaFechaUltimoHbsMayor10',
            searchName: 'filtroFechaUltimoHbsMayor10'
          },
          dataIndex: 'fecha_antihbs_mayor10'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.lastHBSUnder10'),
      dataIndex: 'antihbs_menor10',
      filter: {
        listName: 'listaUltimoHbsMenor10',
        searchName: 'filtroUltimoHbsMenor10',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('antihbs_menor10'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaUltimoHbsMenor10',
            searchName: 'filtroUltimoHbsMenor10'
          },
          dataIndex: 'antihbs_menor10'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.dateLastHBSUnder10'),
      dataIndex: 'fecha_antihbs_menor10',
      filter: {
        listName: 'listaFechaUltimoHbsMenor10',
        searchName: 'filtroFechaUltimoHbsMenor10',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_antihbs_menor10'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaFechaUltimoHbsMenor10',
            searchName: 'filtroFechaUltimoHbsMenor10'
          },
          dataIndex: 'fecha_antihbs_menor10'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.treatmentOptions'),
      dataIndex: 'opciones_tratamiento',
      filter: {
        listName: 'listaOpcionesTratamiento',
        searchName: 'filtroOpcionesTratamiento',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('opciones_tratamiento'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaOpcionesTratamiento',
            searchName: 'filtroOpcionesTratamiento'
          },
          dataIndex: 'opciones_tratamiento'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        controlSeleccionado: classification,
        limit: pageSize,
        offset,
        // filtroOpcionesTratamiento: option,
        ...joinFilter,
        ...joinSorter
      }
    };
    getLossHepatitisImmunityListPlus(data, (_, resp) => {
      if (resp?.data && resp?.data?.totalRegistros != null) {
        setTotalItems(resp?.data?.totalRegistros);
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          const dateUp10 = formatDate(item.fecha_antihbs_mayor10);
          const dateUnder10 = formatDate(item.fecha_antihbs_menor10);
          return {
            ...item,
            fecha_antihbs_mayor10: dateUp10,
            fecha_antihbs_menor10: dateUnder10
          };
        });
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  React.useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  React.useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
LossHepatitisImmunityListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getLossHepatitisImmunityListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default LossHepatitisImmunityListView;
